import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Calendar, Clock, Droplets, MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { calendarService } from '@/services';
import { useAuth } from '@/contexts/AuthContext';
import type { ApiCalendarEvent } from '@/types/api';

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

function IrrigationItem({ event, index }: { event: ApiCalendarEvent; index: number }) {
  const startHour = parseInt(event.startTime?.split(':')[0] || '0');
  const currentHour = new Date().getHours();
  const isNext = startHour > currentHour;
  const isNow = startHour === currentHour;

  return (
    <motion.div
      variants={itemVariants}
      transition={{ delay: index * 0.1 }}
      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
        isNow ? 'bg-primary/20 border border-primary/30' : 
        isNext ? 'bg-muted/50 hover:bg-muted' : 'bg-muted/30 opacity-60'
      }`}
    >
      <div className={`p-2 rounded-lg ${isNow ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
        <Clock className="w-4 h-4" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{event.scheduleName || 'Sin nombre'}</p>
          {isNow && (
            <Badge variant="default" className="text-xs animate-pulse">
              En curso
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <span className="font-mono">{event.startTime || '--:--'}</span>
          <span>•</span>
          <span>{event.duration || 0} min</span>
          <span>•</span>
          <span className="truncate">{event.garden?.name || 'Sin jardín'}</span>
        </div>
      </div>

      <div className="text-right">
        <p className="text-xs text-muted-foreground">{event.controller?.name || 'Sin controlador'}</p>
        <p className="text-xs font-medium">{event.valve?.name || 'Sin válvula'}</p>
      </div>
    </motion.div>
  );
}

export function UpcomingIrrigations() {
  const { currentUser } = useAuth();
  // Obtener companyId si el usuario es company_admin (el backend filtra automáticamente)
  const companyId = currentUser?.role === 'company_admin' ? currentUser.companyId : undefined;
  
  const { data: calendarData, isLoading, error } = useQuery({
    queryKey: ['calendar-today', companyId],
    queryFn: async () => {
      try {
        // El backend filtra automáticamente según el usuario autenticado
        const response = await calendarService.getToday();
        if (!response.success) {
          // Si el endpoint no existe, devolver vacío en lugar de error
          return { events: [], filters: null };
        }
        return response.data;
      } catch (e) {
        // Si hay error de red o endpoint no existe, devolver vacío
        console.warn('Calendar endpoint not available:', e);
        return { events: [], filters: null };
      }
    },
    refetchInterval: 60000, // Refresh every minute
    retry: false, // No reintentar si falla
  });

  const events = calendarData?.events || [];
  
  // Ordenar por hora de inicio
  const sortedEvents = [...events].sort((a, b) => {
    return a.startTime.localeCompare(b.startTime);
  });

  // Mostrar máximo 5 eventos
  const displayEvents = sortedEvents.slice(0, 5);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Riegos de Hoy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Riegos de Hoy
          </CardTitle>
          <Badge variant="secondary">
            {events.length} programado{events.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {displayEvents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Droplets className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No hay riegos programados para hoy</p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } }
            }}
            className="space-y-2"
          >
            {displayEvents.map((event, index) => (
              <IrrigationItem 
                key={`${event.scheduleId}-${index}`} 
                event={event} 
                index={index}
              />
            ))}
            
            {events.length > 5 && (
              <p className="text-center text-xs text-muted-foreground py-2">
                +{events.length - 5} riegos más
              </p>
            )}
          </motion.div>
        )}
        
        <div className="mt-4 pt-3 border-t">
          <Link to="/calendar">
            <Button variant="ghost" size="sm" className="w-full gap-2">
              Ver calendario completo
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
